import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FileMetadata } from '../types/firstProcessing.types';
import { useFileSizeLimit } from '@/hooks/useFileSizeLimit';

/** Calculated time metadata from uploaded file */
export interface TimeMetadata {
  offsetMinutes: number | null;
  timestepMinutes: number | null;
}

/**
 * Parse UTC string to Date object
 * Supports formats: "YYYY-MM-DD HH:MM:SS", "YYYY-MM-DDTHH:MM:SS", "DD.MM.YYYY HH:MM"
 */
const parseUTCString = (utcString: string): Date | null => {
  if (!utcString) return null;
  
  // Try ISO format first
  let date = new Date(utcString);
  if (!isNaN(date.getTime())) return date;
  
  // Try DD.MM.YYYY HH:MM format
  const euroMatch = utcString.match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (euroMatch) {
    const [, day, month, year, hour, minute, second = '0'] = euroMatch;
    date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute), parseInt(second));
    if (!isNaN(date.getTime())) return date;
  }
  
  return null;
};

/**
 * Calculate offset and timestep from table data
 * Offset is calculated as: first timestamp's minute % timestep (matching backend logic)
 */
const calculateTimeMetadataFromTable = (tableData: string[][]): TimeMetadata => {
  // Need at least header + 2 data rows
  if (!tableData || tableData.length < 3) {
    return { offsetMinutes: null, timestepMinutes: null };
  }

  // Find UTC column index (usually first column)
  const headers = tableData[0];
  const utcColIndex = headers.findIndex(h => h.toLowerCase() === 'utc');
  if (utcColIndex === -1) {
    return { offsetMinutes: null, timestepMinutes: null };
  }

  // Parse first and second data row UTC values
  const firstUTC = tableData[1]?.[utcColIndex];
  const secondUTC = tableData[2]?.[utcColIndex];

  const firstDate = parseUTCString(firstUTC);
  const secondDate = parseUTCString(secondUTC);

  if (!firstDate || !secondDate) {
    return { offsetMinutes: null, timestepMinutes: null };
  }

  // Calculate timestep first: difference between first two data points in minutes
  const timestepMinutes = Math.round((secondDate.getTime() - firstDate.getTime()) / (1000 * 60));

  // Calculate offset: minute within the hour, modulo timestep (matches backend logic)
  // Backend: offset = first_time.minute % time_step
  const offsetMinutes = timestepMinutes > 0 ? firstDate.getMinutes() % timestepMinutes : 0;

  return { offsetMinutes, timestepMinutes };
};

interface UseFirstProcessingFileReturn {
  fileContent: string | null;
  fileMetadata: FileMetadata | null;
  timeMinRaw: string;
  timeMaxRaw: string;
  tableData: string[][];
  timeMetadata: TimeMetadata;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  isProcessing: boolean;
  error: string | null;
  selectedFiles: File[];
  firstTwoLines: { [key: string]: string };
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  processUtcTimes: (content: string) => boolean;
  resetFile: () => void;
  clearError: () => void;
}

export interface UseFirstProcessingFileOptions {
  onErrorPayload?: (payload: { message: string; code?: string; suggestion?: string }) => void;
}

export const useFirstProcessingFile = (options?: UseFirstProcessingFileOptions): UseFirstProcessingFileReturn => {
  const { onErrorPayload } = options ?? {};
  const { t } = useTranslation('firstProcessing');
  const { t: tDataProcessing } = useTranslation('dataProcessing');
  const { maxFileSizeBytes, formattedMaxSize } = useFileSizeLimit();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [fileMetadata, setFileMetadata] = useState<FileMetadata | null>(null);
  const [timeMinRaw, setTimeMinRaw] = useState<string>('');
  const [timeMaxRaw, setTimeMaxRaw] = useState<string>('');
  const [tableData, setTableData] = useState<string[][]>([]);
  const [timeMetadata, setTimeMetadata] = useState<TimeMetadata>({ offsetMinutes: null, timestepMinutes: null });
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [firstTwoLines, setFirstTwoLines] = useState<{ [key: string]: string }>({});

  const formatDate = useCallback((date: Date): string => {
    const pad = (num: number) => String(num).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds()
    )}`;
  }, []);

  const processUtcTimes = useCallback((content: string): boolean => {
    const lines = content.split('\n');
    let minDate = new Date(8640000000000000); // Max possible date
    let maxDate = new Date(-8640000000000000); // Min possible date

    // Skip header line
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line) {
        const [utcStr] = line.split(';');
        if (utcStr) {
          // Parse date and time in format "YYYY-MM-DD HH:mm:ss"
          const [datePart, timePart] = utcStr.trim().split(' ');
          if (datePart && timePart) {
            const [year, month, day] = datePart.split('-').map(Number);
            const [hours, minutes, seconds] = timePart.split(':').map(Number);

            const utcDate = new Date(
              year,
              month - 1,
              day,
              hours,
              minutes,
              seconds
            );

            if (!isNaN(utcDate.getTime())) {
              if (utcDate < minDate) minDate = utcDate;
              if (utcDate > maxDate) maxDate = utcDate;
            }
          }
        }
      }
    }

    // Set min and max dates only if at least one row parsed successfully
    if (minDate.getTime() !== 8640000000000000) {
      setTimeMinRaw(formatDate(minDate));
      setTimeMaxRaw(formatDate(maxDate));
      return true;
    }
    return false;
  }, [formatDate]);

  const processFileContent = useCallback((content: string): string[][] => {
    const lines = content.split('\n');
    
    // Clean and prepare table data
    const cleanedLines = lines.map((line) => line.trim().replace('\r', ''));
    const separator = cleanedLines[0]?.includes(';') ? ';' : ',';
    
    // Parse and clean table data
    const tableRows = cleanedLines
      .filter((line) => line !== '')
      .map((line) => {
        const cells = line.split(separator);
        return cells.map((cell) => cell.trim());
      });
    
    return tableRows;
  }, []);

  const handleFileChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      // Reset all state when file is removed
      setFileContent(null);
      setFileMetadata(null);
      setTimeMinRaw('');
      setTimeMaxRaw('');
      setTableData([]);
      setTimeMetadata({ offsetMinutes: null, timestepMinutes: null });
      setError(null);
      setIsProcessing(false);
      setSelectedFiles([]);
      setFirstTwoLines({});
      return;
    }

    // Validate only one file is allowed
    if (files.length > 1) {
      setError(t('multipleFilesNotAllowed'));
      setIsProcessing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsProcessing(true);
    setError(null);
    setSelectedFiles(Array.from(files));

    const file = files[0];
    
    // Validate file type
    const allowedExtensions = ['.csv'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedExtensions.includes(fileExtension)) {
      setError(t('invalidFileType', { allowed: allowedExtensions.join(', ') }));
      setIsProcessing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (dynamic limit based on subscription plan)
    if (file.size > maxFileSizeBytes) {
      setError(tDataProcessing('dataProcessing.errors.fileSizeExceeded', { maxSize: formattedMaxSize }));
      setIsProcessing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    try {
      const reader = new FileReader();

      const content = await new Promise<string>((resolve, reject) => {
        reader.onload = (e) => {
          const result = e.target?.result as string;
          resolve(result);
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      });

      // CSV Validation 1: Empty file
      if (!content || content.trim() === '') {
        setError(t('emptyFile'));
        setIsProcessing(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const lines = content.split(/\r?\n/).filter(Boolean);

      // CSV Validation 2: Minimum 2 lines (header + at least 1 data row)
      if (lines.length < 2) {
        setError(t('noDataRow'));
        setIsProcessing(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Auto-detect delimiter
      const separator = lines[0].includes(';') ? ';' : ',';
      const headers = lines[0].split(separator).map((h) => h.trim()).filter(h => h.length > 0);

      // CSV Validation 3: Must have exactly 2 columns
      if (headers.length !== 2) {
        setError(t('mustHave2Columns', { count: headers.length }));
        setIsProcessing(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // CSV Validation 4: Must have UTC column (exact name match)
      const hasUtcColumn = headers.some(header => {
        const lowerHeader = header.toLowerCase();
        return lowerHeader === 'utc';
      });

      if (!hasUtcColumn) {
        setError(t('mustHaveUtcColumn'));
        setIsProcessing(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // CSV Validation 5: Timestamps must be parseable in '%Y-%m-%d %H:%M:%S'.
      // processUtcTimes returns false if it found ZERO rows it could parse —
      // which means we'd be advancing the user into Step 2 with a file the
      // backend will reject after 4 retries (~30s of silent UI lock).
      // Reject at the picker boundary with an actionable i18n error instead.
      const timestampsOk = processUtcTimes(content);
      if (!timestampsOk) {
        setError(t('invalidTimestampFormat'));
        setIsProcessing(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      setFileContent(content);
      setFileMetadata({
        name: file.name,
        size: file.size,
        lastModified: file.lastModified,
        content: content
      });

      // Extract first two lines for preview
      const linesForPreview = content.split('\n');
      const preview = linesForPreview.slice(0, 2).join('\n');
      setFirstTwoLines({ [file.name]: preview });

      const tableRows = processFileContent(content);
      setTableData(tableRows);

      // Calculate time metadata from parsed table
      const metadata = calculateTimeMetadataFromTable(tableRows);
      setTimeMetadata(metadata);

    } catch (err) {
      const e = err as Error & { code?: string; suggestion?: string };
      const message = e.message || t('failedToProcess');
      setError(message); // keep legacy for now — C4 page wires reducer too
      onErrorPayload?.({ message, code: e.code, suggestion: e.suggestion });
    } finally {
      setIsProcessing(false);
    }
  }, [processUtcTimes, processFileContent, t, tDataProcessing, maxFileSizeBytes, formattedMaxSize]);

  const resetFile = useCallback(() => {
    setFileContent(null);
    setFileMetadata(null);
    setTimeMinRaw('');
    setTimeMaxRaw('');
    setTableData([]);
    setTimeMetadata({ offsetMinutes: null, timestepMinutes: null });
    setError(null);
    setIsProcessing(false);
    setSelectedFiles([]);
    setFirstTwoLines({});
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    fileContent,
    fileMetadata,
    timeMinRaw,
    timeMaxRaw,
    tableData,
    timeMetadata,
    fileInputRef,
    isProcessing,
    error,
    selectedFiles,
    firstTwoLines,
    handleFileChange,
    processUtcTimes,
    resetFile,
    clearError
  };
};